// 精选的 MetaCubeX/meta-rules-dat 规则目录。所有 name 均已核对存在于 meta 分支 geo/geosite 与 geo/geoip 下。

export type SetType = 'geosite' | 'geoip';

export interface RuleSetRef {
  type: SetType;
  name: string;
  /** geoip 默认 no-resolve;只有兜底的 cn IP 需要解析 */
  resolve?: boolean;
}

export type GroupKey =
  | 'ads'
  | 'ai'
  | 'social'
  | 'streaming'
  | 'games'
  | 'google'
  | 'microsoft'
  | 'apple'
  | 'dev'
  | 'proxy'
  | 'direct';

export type GroupKind = 'proxy' | 'direct' | 'reject';

export interface GroupDef {
  key: GroupKey;
  name: string;
  kind: GroupKind;
}

// proxy 指向「🚀 节点选择」本身,不单独建组
export const GROUPS: GroupDef[] = [
  { key: 'ai', name: '🤖 AI 服务', kind: 'proxy' },
  { key: 'streaming', name: '📺 流媒体', kind: 'proxy' },
  { key: 'social', name: '💬 社交通讯', kind: 'proxy' },
  { key: 'games', name: '🎮 游戏平台', kind: 'proxy' },
  { key: 'google', name: '🔍 谷歌服务', kind: 'proxy' },
  { key: 'microsoft', name: 'Ⓜ️ 微软服务', kind: 'proxy' },
  { key: 'apple', name: '🍎 苹果服务', kind: 'direct' },
  { key: 'dev', name: '🧑‍💻 开发者服务', kind: 'proxy' },
  { key: 'direct', name: '🎯 全球直连', kind: 'direct' },
  { key: 'ads', name: '🛑 广告拦截', kind: 'reject' },
  { key: 'proxy', name: '🚀 节点选择', kind: 'proxy' },
];

export const PROXY_GROUP_NAME = '🚀 节点选择';
export const AUTO_GROUP_NAME = '♻️ 自动选择';
export const FINAL_GROUP_NAME = '🐟 漏网之鱼';

export interface RuleItem {
  id: string;
  label: string;
  desc?: string;
  sets: RuleSetRef[];
  group: GroupKey;
  /** 越小越靠前;具体规则必须排在宽泛规则之前 */
  order: number;
  defaultOn?: boolean;
  /**
   * 该规则覆盖了哪些规则的域名:排在它们上方且去向不同时给出冲突提示。
   * overseas 表示覆盖所有海外服务类规则(非中国域名、GFW 这类兜底)
   */
  covers?: string[] | 'overseas';
}

export interface Category {
  key: string;
  label: string;
  hint: string;
  items: RuleItem[];
}

const gs = (name: string): RuleSetRef => ({ type: 'geosite', name });
const ip = (name: string, resolve = false): RuleSetRef => ({ type: 'geoip', name, resolve });

export const CATEGORIES: Category[] = [
  {
    key: 'basic',
    label: '基础',
    hint: '局域网直连、广告拦截',
    items: [
      { id: 'ads', label: '广告拦截', desc: 'category-ads-all', sets: [gs('category-ads-all')], group: 'ads', order: 10, defaultOn: true },
      { id: 'private', label: '局域网/私有地址', desc: '内网域名与 IP 直连', sets: [gs('private'), ip('private')], group: 'direct', order: 20, defaultOn: true },
    ],
  },
  {
    key: 'ai',
    label: 'AI',
    hint: '合集已覆盖主流 AI,单项可按需补充',
    items: [
      { id: 'ai-all', label: '海外 AI 合集', desc: 'ChatGPT/Claude/Gemini/Copilot 等', sets: [gs('category-ai-!cn')], group: 'ai', order: 400, defaultOn: true, covers: ['openai', 'anthropic', 'gemini', 'perplexity', 'huggingface', 'cursor'] },
      { id: 'openai', label: 'OpenAI', sets: [gs('openai')], group: 'ai', order: 401 },
      { id: 'anthropic', label: 'Anthropic / Claude', sets: [gs('anthropic')], group: 'ai', order: 402 },
      { id: 'gemini', label: 'Google Gemini', sets: [gs('google-gemini')], group: 'ai', order: 403 },
      { id: 'perplexity', label: 'Perplexity', sets: [gs('perplexity')], group: 'ai', order: 404 },
      { id: 'huggingface', label: 'Hugging Face', sets: [gs('huggingface')], group: 'ai', order: 405 },
      { id: 'cursor', label: 'Cursor', sets: [gs('cursor')], group: 'ai', order: 406 },
    ],
  },
  {
    key: 'bigtech',
    label: '科技大厂',
    hint: '各自独立策略组,可在客户端单独切换',
    items: [
      { id: 'google', label: 'Google', desc: '含 Google IP', sets: [gs('google'), ip('google')], group: 'google', order: 800, defaultOn: true, covers: ['google-cn', 'gemini'] },
      { id: 'microsoft', label: 'Microsoft', desc: '含 OneDrive', sets: [gs('microsoft'), gs('onedrive')], group: 'microsoft', order: 810, defaultOn: true, covers: ['microsoft-cn'] },
      { id: 'apple', label: 'Apple', desc: '含 iCloud', sets: [gs('apple'), gs('icloud')], group: 'apple', order: 820, defaultOn: true, covers: ['apple-cn'] },
      { id: 'github', label: 'GitHub', sets: [gs('github')], group: 'dev', order: 830, defaultOn: true },
      { id: 'amazon', label: 'Amazon / AWS', sets: [gs('amazon'), gs('aws')], group: 'proxy', order: 840 },
      { id: 'cloudflare', label: 'Cloudflare', desc: '含 Cloudflare IP', sets: [gs('cloudflare'), ip('cloudflare')], group: 'proxy', order: 850 },
      { id: 'meta', label: 'Meta', desc: 'Facebook/Instagram/WhatsApp', sets: [gs('facebook'), gs('instagram'), gs('whatsapp'), ip('facebook')], group: 'social', order: 520 },
    ],
  },
  {
    key: 'social',
    label: '社交通讯',
    hint: '',
    items: [
      { id: 'telegram', label: 'Telegram', desc: '含 Telegram IP', sets: [gs('telegram'), ip('telegram')], group: 'social', order: 500, defaultOn: true },
      { id: 'twitter', label: 'X / Twitter', desc: '含 Twitter IP', sets: [gs('twitter'), ip('twitter')], group: 'social', order: 510, defaultOn: true },
      { id: 'discord', label: 'Discord', sets: [gs('discord')], group: 'social', order: 530 },
      { id: 'reddit', label: 'Reddit', sets: [gs('reddit')], group: 'social', order: 540 },
      { id: 'line', label: 'LINE', sets: [gs('line')], group: 'social', order: 550 },
      { id: 'social-all', label: '海外社交合集', desc: 'category-social-media-!cn', sets: [gs('category-social-media-!cn')], group: 'social', order: 590, covers: ['telegram', 'twitter', 'discord', 'reddit', 'line', 'meta'] },
    ],
  },
  {
    key: 'streaming',
    label: '流媒体',
    hint: '',
    items: [
      { id: 'youtube', label: 'YouTube', sets: [gs('youtube')], group: 'streaming', order: 600, defaultOn: true },
      { id: 'netflix', label: 'Netflix', desc: '含 Netflix IP', sets: [gs('netflix'), ip('netflix')], group: 'streaming', order: 610, defaultOn: true },
      { id: 'disney', label: 'Disney+', sets: [gs('disney')], group: 'streaming', order: 620 },
      { id: 'hbo', label: 'HBO Max', sets: [gs('hbo')], group: 'streaming', order: 630 },
      { id: 'primevideo', label: 'Prime Video', sets: [gs('primevideo')], group: 'streaming', order: 640 },
      { id: 'spotify', label: 'Spotify', sets: [gs('spotify')], group: 'streaming', order: 650, defaultOn: true },
      { id: 'twitch', label: 'Twitch', sets: [gs('twitch')], group: 'streaming', order: 660 },
      { id: 'tiktok', label: 'TikTok', sets: [gs('tiktok')], group: 'streaming', order: 670 },
      { id: 'bahamut', label: '巴哈姆特', desc: '需台湾节点', sets: [gs('bahamut')], group: 'streaming', order: 680 },
      { id: 'abema', label: 'AbemaTV', desc: '需日本节点', sets: [gs('abema')], group: 'streaming', order: 690 },
      { id: 'biliintl', label: 'Bilibili 国际版', sets: [gs('biliintl')], group: 'streaming', order: 695 },
    ],
  },
  {
    key: 'games',
    label: '游戏',
    hint: '国内 CDN 下载可在「国内直连」中开启',
    items: [
      { id: 'steam', label: 'Steam', sets: [gs('steam')], group: 'games', order: 700, defaultOn: true, covers: ['steam-cn'] },
      { id: 'epic', label: 'Epic Games', sets: [gs('epicgames')], group: 'games', order: 710 },
      { id: 'ea', label: 'EA', sets: [gs('ea')], group: 'games', order: 720 },
      { id: 'blizzard', label: '暴雪 Blizzard', sets: [gs('blizzard')], group: 'games', order: 730 },
      { id: 'ubisoft', label: '育碧 Ubisoft', sets: [gs('ubisoft')], group: 'games', order: 740 },
      { id: 'nintendo', label: 'Nintendo', sets: [gs('nintendo')], group: 'games', order: 750 },
      { id: 'playstation', label: 'PlayStation', sets: [gs('playstation')], group: 'games', order: 760 },
      { id: 'xbox', label: 'Xbox', sets: [gs('xbox')], group: 'games', order: 770 },
      { id: 'riot', label: 'Riot Games', sets: [gs('riot')], group: 'games', order: 780 },
      { id: 'hoyoverse', label: 'HoYoverse 国际服', sets: [gs('hoyoverse')], group: 'games', order: 785 },
      { id: 'games-all', label: '海外游戏合集', desc: 'category-games-!cn', sets: [gs('category-games-!cn')], group: 'games', order: 795, covers: ['steam', 'epic', 'ea', 'blizzard', 'ubisoft', 'nintendo', 'playstation', 'xbox', 'riot', 'hoyoverse'] },
    ],
  },
  {
    key: 'dev',
    label: '开发者',
    hint: '',
    items: [
      { id: 'gitlab', label: 'GitLab', sets: [gs('gitlab')], group: 'dev', order: 900 },
      { id: 'docker', label: 'Docker', sets: [gs('docker')], group: 'dev', order: 910 },
      { id: 'npm', label: 'npm', sets: [gs('npmjs')], group: 'dev', order: 920 },
      { id: 'python', label: 'Python / PyPI', sets: [gs('python')], group: 'dev', order: 930 },
      { id: 'jetbrains', label: 'JetBrains', sets: [gs('jetbrains')], group: 'dev', order: 940 },
      { id: 'dev-all', label: '开发者合集', desc: 'category-dev', sets: [gs('category-dev')], group: 'dev', order: 990, covers: ['github', 'gitlab', 'docker', 'npm', 'python', 'jetbrains'] },
    ],
  },
  {
    key: 'overseas',
    label: '海外常用',
    hint: '走「🚀 节点选择」',
    items: [
      { id: 'paypal', label: 'PayPal', sets: [gs('paypal')], group: 'proxy', order: 1000 },
      { id: 'notion', label: 'Notion', sets: [gs('notion')], group: 'proxy', order: 1010 },
      { id: 'slack', label: 'Slack', sets: [gs('slack')], group: 'proxy', order: 1020 },
      { id: 'zoom', label: 'Zoom', sets: [gs('zoom')], group: 'proxy', order: 1030 },
      { id: 'dropbox', label: 'Dropbox', sets: [gs('dropbox')], group: 'proxy', order: 1040 },
      { id: 'wikimedia', label: '维基百科', sets: [gs('wikimedia')], group: 'proxy', order: 1050 },
      { id: 'pixiv', label: 'Pixiv', sets: [gs('pixiv')], group: 'proxy', order: 1060 },
      { id: 'crypto', label: '加密货币', desc: 'category-cryptocurrency', sets: [gs('category-cryptocurrency')], group: 'proxy', order: 1070 },
      { id: 'scholar', label: '海外学术', desc: 'category-scholar-!cn', sets: [gs('category-scholar-!cn')], group: 'proxy', order: 1080 },
    ],
  },
  {
    key: 'domestic',
    label: '国内直连',
    hint: '大厂的国内服务需排在大厂规则之前,已自动处理顺序',
    items: [
      { id: 'microsoft-cn', label: '微软国内服务', desc: 'microsoft@cn', sets: [gs('microsoft@cn')], group: 'direct', order: 300, defaultOn: true },
      { id: 'apple-cn', label: '苹果国内服务', desc: 'apple-cn', sets: [gs('apple-cn')], group: 'direct', order: 310, defaultOn: true },
      { id: 'google-cn', label: '谷歌国内服务', desc: 'google-cn', sets: [gs('google-cn')], group: 'direct', order: 320, defaultOn: true },
      { id: 'steam-cn', label: 'Steam 国内 CDN', desc: 'steam@cn', sets: [gs('steam@cn')], group: 'direct', order: 330, defaultOn: true },
      { id: 'games-cn', label: '游戏国内 CDN', desc: 'category-games@cn', sets: [gs('category-games@cn')], group: 'direct', order: 340 },
      { id: 'pt', label: 'PT 站点', desc: 'category-pt', sets: [gs('category-pt')], group: 'direct', order: 350 },
      { id: 'tracker', label: 'BT Tracker', desc: 'category-public-tracker', sets: [gs('category-public-tracker')], group: 'direct', order: 360 },
      { id: 'cn-site', label: '国内域名', desc: 'geosite:cn', sets: [gs('cn')], group: 'direct', order: 1200, defaultOn: true, covers: ['microsoft-cn', 'apple-cn', 'google-cn', 'steam-cn', 'games-cn'] },
      { id: 'cn-ip', label: '国内 IP', desc: 'geoip:cn(兜底,会触发解析)', sets: [ip('cn', true)], group: 'direct', order: 1210, defaultOn: true },
    ],
  },
  {
    key: 'global',
    label: '海外兜底',
    hint: '二选一即可:非中国域名 覆盖面更广',
    items: [
      { id: 'not-cn', label: '非中国域名', desc: 'geolocation-!cn', sets: [gs('geolocation-!cn')], group: 'proxy', order: 1100, defaultOn: true, covers: 'overseas' },
      { id: 'gfw', label: 'GFW 列表', desc: 'gfw', sets: [gs('gfw')], group: 'proxy', order: 1110, covers: 'overseas' },
    ],
  },
];

export const ALL_ITEMS: RuleItem[] = CATEGORIES.flatMap((c) => c.items);
export const ITEM_BY_ID = new Map(ALL_ITEMS.map((i) => [i.id, i]));

/** 允许通过本站代理下载的规则文件白名单 */
export const ALLOWED_SETS = new Set(ALL_ITEMS.flatMap((i) => i.sets.map((s) => `${s.type}/${s.name}`)));

export type RegionKey = 'hk' | 'tw' | 'jp' | 'sg' | 'us' | 'kr';

export interface RegionDef {
  key: RegionKey;
  name: string;
  /** 不含 (?i) 前缀;输出到 YAML 时补上 */
  pattern: string;
}

export const REGIONS: RegionDef[] = [
  { key: 'hk', name: '🇭🇰 香港', pattern: '港|🇭🇰|\\bHK\\b|Hong ?Kong' },
  { key: 'tw', name: '🇹🇼 台湾', pattern: '台|🇹🇼|\\bTW\\b|Taiwan' },
  { key: 'jp', name: '🇯🇵 日本', pattern: '日本|东京|大阪|🇯🇵|\\bJP\\b|Japan' },
  { key: 'sg', name: '🇸🇬 新加坡', pattern: '新加坡|狮城|🇸🇬|\\bSG\\b|Singapore' },
  { key: 'us', name: '🇺🇸 美国', pattern: '美国|美國|🇺🇸|\\bUS\\b|United States|America' },
  { key: 'kr', name: '🇰🇷 韩国', pattern: '韩国|韓國|🇰🇷|\\bKR\\b|Korea' },
];

/** 机场常见的信息节点(剩余流量、到期时间等),不参与分组 */
export const INFO_NODE_PATTERN = '剩余|流量|到期|过期|官网|套餐|重置|Expire|Traffic';

export type MirrorKey = 'fastly' | 'testingcf' | 'jsdelivr' | 'gcore' | 'github' | 'self';

export const MIRRORS: { key: MirrorKey; label: string; base: string }[] = [
  { key: 'fastly', label: 'jsDelivr Fastly', base: 'https://fastly.jsdelivr.net/gh/MetaCubeX/meta-rules-dat@meta/geo' },
  { key: 'testingcf', label: 'jsDelivr Cloudflare', base: 'https://testingcf.jsdelivr.net/gh/MetaCubeX/meta-rules-dat@meta/geo' },
  { key: 'gcore', label: 'jsDelivr Gcore', base: 'https://gcore.jsdelivr.net/gh/MetaCubeX/meta-rules-dat@meta/geo' },
  { key: 'jsdelivr', label: 'jsDelivr 官方', base: 'https://cdn.jsdelivr.net/gh/MetaCubeX/meta-rules-dat@meta/geo' },
  { key: 'github', label: 'GitHub Raw(需代理)', base: 'https://raw.githubusercontent.com/MetaCubeX/meta-rules-dat/meta/geo' },
  { key: 'self', label: '本站缓存代理', base: '' },
];

export type DnsPreset = 'fakeip' | 'fakeip-safe' | 'redir-host' | 'none';

export const DNS_PRESETS: { key: DnsPreset; label: string; desc: string }[] = [
  { key: 'fakeip-safe', label: 'Fake-IP + 防泄露', desc: '国内域名用国内 DoH,海外域名经代理用 Cloudflare/Google DoH(推荐)' },
  { key: 'fakeip', label: 'Fake-IP', desc: '全部使用国内 DoH 解析,速度快' },
  { key: 'redir-host', label: 'Redir-Host', desc: '返回真实 IP,兼容性最好' },
  { key: 'none', label: '不写入 DNS', desc: '使用客户端自身的 DNS 设置' },
];
